/*
Created-by: XtroEdge AI
Updated-by: XtroEdge AI
Last modified: 2026-11-03T01:15:00+05:00
Intent: Build the survey responses workbook in the agreed format (Survey_Responses_Sample / _Anonymous): a Summary, an All answers sheet and one sheet per survey level, with the sample's styling, formulas, filters and frozen panes. exceljs is loaded on demand to keep it out of the initial bundle; loadExcelWorkbook reads its Workbook class from `default` too, as the production build exposes it there.
*/

// Type-only: the library itself is imported in writeSurveyWorkbook, so it lands in its own
// chunk and is only downloaded when an export actually runs.
import type { Workbook, Worksheet } from 'exceljs';
import { translateCountryName } from './country-i18n.util';

export type SurveyExportLang = 'en' | 'ar';
export type SurveyCell = string | number | Date | null;

/** One answer as the API sent it, before it is matched to a question. */
export interface SurveyRawAnswer {
  questionId?: string;
  question?: string;
  value: unknown;
}

export interface SurveyLevelInput {
  id: string;
  position: number;
  name: string;
  arabicName?: string;
  questions: any[];
}

export interface SurveyRespondent {
  id: string | number | null;
  name: string;
  phone: string;
  email: string;
  gender: string;
  age: number | string | null;
  country: string;
  /** What the respondent submitted, by survey level id. */
  submissions: Record<string, { submittedOn: Date | null; answers: SurveyRawAnswer[] }>;
}

export interface SurveySheet {
  name: string;
  headers: string[];
  rows: SurveyCell[][];
}

export interface SurveySummaryRow {
  level: string;
  q: string;
  question: string;
  type: string;
  answered: number;
  option: string;
  responses: number;
  /** Share of the people who answered the question, 0–1. Blank for open text. */
  percent: number | null;
  open: boolean;
  levelSheet: string;
  /** 0-based column of the question on its level sheet. */
  levelColumn: number;
}

export interface SurveyExport {
  rtl: boolean;
  summary: { name: string; title: string; note: string; headers: string[]; rows: SurveySummaryRow[] };
  allAnswers: SurveySheet & { levelColumn: number; qColumn: number; answerColumn: number };
  levels: SurveySheet[];
}

const LABELS = {
  en: {
    summary: 'Summary',
    allAnswers: 'All answers',
    level: 'Level',
    title: 'Survey results summary',
    note: (allAnswers: string) => `Counts update from the ${allAnswers} sheet. % = share of participants who answered the question; questions that allow several answers can add up to more than 100%.`,
    anonymousNote: 'Anonymous survey: participant details are hidden and responses are in random order.',
    surveyLevel: 'Survey level',
    qNumber: 'Q#',
    qPrefix: 'Q',
    question: 'Question',
    questionType: 'Question type',
    answered: 'Answered',
    answerOption: 'Answer option',
    responses: 'Responses',
    percent: '%',
    participant: 'Participant',
    responseHeader: 'Response',
    phone: 'Phone number',
    email: 'Email',
    gender: 'Gender',
    age: 'Age',
    country: 'Country',
    submittedOn: 'Submitted on',
    answer: 'Answer',
    single: 'Multiple choice (one answer)',
    multiple: 'Multiple choice (several answers)',
    open: 'Open text',
    openOption: '(open text — see the level sheet)',
    participantId: (id: string) => `Participant #${id}`,
    response: (n: number) => `Response ${n}`,
    male: 'Male',
    female: 'Female',
  },
  ar: {
    summary: 'الملخص',
    allAnswers: 'كل الإجابات',
    level: 'المستوى',
    title: 'ملخص نتائج الاستبيان',
    note: (allAnswers: string) => `تُحدَّث الأعداد من ورقة ${allAnswers}. النسبة % = حصة المشاركين الذين أجابوا على السؤال؛ قد يتجاوز مجموع الأسئلة التي تسمح بعدة إجابات 100%.`,
    anonymousNote: 'استبيان مجهول الهوية: بيانات المشاركين مخفية والردود بترتيب عشوائي.',
    surveyLevel: 'مستوى الاستبيان',
    qNumber: 'س#',
    qPrefix: 'س',
    question: 'السؤال',
    questionType: 'نوع السؤال',
    answered: 'عدد من أجابوا',
    answerOption: 'خيار الإجابة',
    responses: 'الردود',
    percent: '%',
    participant: 'المشارك',
    responseHeader: 'الرد',
    phone: 'رقم الهاتف',
    email: 'البريد الإلكتروني',
    gender: 'الجنس',
    age: 'العمر',
    country: 'الدولة',
    submittedOn: 'تاريخ الإرسال',
    answer: 'الإجابة',
    single: 'اختيار من متعدد (إجابة واحدة)',
    multiple: 'اختيار من متعدد (عدة إجابات)',
    open: 'نص مفتوح',
    openOption: '(نص مفتوح — راجع ورقة المستوى)',
    participantId: (id: string) => `المشارك #${id}`,
    response: (n: number) => `الرد ${n}`,
    male: 'ذكر',
    female: 'أنثى',
  },
};

/** Several answers in one cell are separated with this, never commas. */
export const ANSWER_SEPARATOR = ' | ';
const DATE_FORMAT = 'dd mmm yyyy hh:mm';

const norm = (value: unknown) => String(value ?? '').trim().toLowerCase();

/**
 * The answers in whatever shape the API stored them: a plain string for a
 * one-question survey, an array of {question, answer} items, the grouped
 * {level_id, questions: [...]} shape, or an object keyed by question.
 */
export function readSurveyAnswers(raw: unknown): SurveyRawAnswer[] {
  if (raw == null || raw === '') return [];
  if (typeof raw === 'string') {
    const text = raw.trim();
    if (text.startsWith('[') || text.startsWith('{')) {
      try {
        return readSurveyAnswers(JSON.parse(text));
      } catch {
        // Not JSON after all, so it is the answer itself.
      }
    }
    return text ? [{ value: text }] : [];
  }
  if (Array.isArray(raw)) {
    if (raw.some((item: any) => Array.isArray(item?.questions))) {
      return raw.flatMap((item: any) => readSurveyAnswers(item?.questions || []));
    }
    const answers: SurveyRawAnswer[] = raw.map((item: any) => {
      if (item === null || typeof item !== 'object') return { value: item };
      const id = item.question_id ?? item.questionId ?? item.survey_question_id ?? item.id;
      const question = item.question ?? item.question_text;
      return {
        questionId: id == null ? undefined : String(id),
        question: question == null || typeof question === 'object' ? undefined : String(question),
        value: item.answer ?? item.value ?? item.answer_display ?? item.answer_text
          ?? item.selected_option ?? item.selected_options ?? item.option_text ?? item.arabic_option_text
          ?? item.option_id ?? item.option_ids,
      };
    });
    // Some answers come as one row per picked option; a question's rows are one answer.
    const byQuestion = new Map<string, SurveyRawAnswer>();
    return answers.filter(answer => {
      if (answer.questionId == null) return true;
      const first = byQuestion.get(answer.questionId);
      if (!first) {
        byQuestion.set(answer.questionId, answer);
        return true;
      }
      first.value = [first.value, answer.value].flat();
      return false;
    });
  }
  if (typeof raw === 'object') {
    return Object.keys(raw as object).map(key => ({ question: key, value: (raw as any)[key] }));
  }
  return [{ value: raw }];
}

type QuestionKind = 'single' | 'multiple' | 'open';

function questionKind(question: any): QuestionKind {
  const type = norm(question?.question_type);
  const hasOptions = Array.isArray(question?.options) && question.options.length > 0;
  if (type === 'open_text' || type === 'text' || !hasOptions) return 'open';
  return question?.is_multi_select === true || norm(question?.answer_selection_type) === 'multiple'
    ? 'multiple' : 'single';
}

function optionText(option: any, lang: SurveyExportLang): string {
  return String((lang === 'ar' ? option?.arabic_option_text || option?.option_text : option?.option_text || option?.arabic_option_text) ?? '').trim();
}

function findOption(question: any, value: string): any {
  const key = norm(value);
  return (question?.options || []).find((option: any) =>
    (option?.id != null && norm(option.id) === key)
    || norm(option?.option_text) === key
    || norm(option?.arabic_option_text) === key);
}

function valueParts(value: unknown): string[] {
  if (value == null) return [];
  if (Array.isArray(value)) return value.flatMap(valueParts);
  if (typeof value === 'object') {
    const item: any = value;
    return valueParts(item.option_text ?? item.text ?? item.label ?? item.value ?? item.answer ?? item.id);
  }
  if (typeof value === 'string' && value.trim().startsWith('[')) {
    try {
      return valueParts(JSON.parse(value));
    } catch {
      // A bracket in a typed answer.
    }
  }
  const text = String(value).trim();
  return text ? [text] : [];
}

/** The answer to one question in the dashboard language: an option list for choices, the text for open questions. */
function resolveAnswer(question: any, kind: QuestionKind, value: unknown, lang: SurveyExportLang): string[] {
  let parts = valueParts(value);
  if (kind === 'open') return parts.length ? [parts.join(ANSWER_SEPARATOR)] : [];
  // A stored "A, B" or "A | B" is split only when every piece is an option,
  // so an option that itself contains a comma stays whole.
  if (kind === 'multiple' && parts.length === 1 && !findOption(question, parts[0])) {
    for (const separator of ['|', ',']) {
      const pieces = parts[0].split(separator).map(piece => piece.trim()).filter(Boolean);
      if (pieces.length > 1 && pieces.every(piece => findOption(question, piece))) {
        parts = pieces;
        break;
      }
    }
  }
  const resolved = parts.map(part => {
    const option = findOption(question, part);
    return option ? optionText(option, lang) || part : part;
  });
  return [...new Set(resolved)];
}

/** Pairs each question with its answer: by question id, then question text, then position. */
function matchAnswers(questions: any[], answers: SurveyRawAnswer[]): (SurveyRawAnswer | undefined)[] {
  const used = new Set<number>();
  const take = (index: number) => {
    if (index < 0) return undefined;
    used.add(index);
    return answers[index];
  };
  const byId = questions.map(question => question?.id == null ? -1
    : answers.findIndex((answer, index) => !used.has(index) && answer.questionId === String(question.id)));
  const matched = byId.map(take);
  return questions.map((question, qIndex) => {
    if (matched[qIndex]) return matched[qIndex];
    const texts = [norm(question?.question_text), norm(question?.arabic_question_text)].filter(Boolean);
    const byText = answers.findIndex((answer, index) => !used.has(index) && !!answer.question && texts.includes(norm(answer.question)));
    if (byText >= 0) return take(byText);
    const sameSpot = answers[qIndex];
    const pointsElsewhere = !!sameSpot && (sameSpot.questionId != null || !!sameSpot.question);
    return !used.has(qIndex) && sameSpot && !pointsElsewhere ? take(qIndex) : undefined;
  });
}

function localizeGender(gender: string, lang: SurveyExportLang): string {
  const key = norm(gender);
  if (key === 'male' || key === 'm') return LABELS[lang].male;
  if (key === 'female' || key === 'f') return LABELS[lang].female;
  return String(gender ?? '').trim();
}

function blankIfMissing(value: unknown): string | number | null {
  if (value == null) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const text = String(value).trim();
  if (!text || ['n/a', 'na', '-', 'null', 'undefined'].includes(text.toLowerCase())) return null;
  return /^\d+$/.test(text) ? Number(text) : text;
}

function shuffle<T>(items: T[], random: () => number): T[] {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

function uniqueSheetName(name: string, taken: Set<string>): string {
  const base = name.replace(/[\[\]:*?\/\\]/g, ' ').trim().slice(0, 31) || 'Sheet';
  let candidate = base;
  for (let n = 2; taken.has(candidate.toLowerCase()); n++) {
    const suffix = ` (${n})`;
    candidate = base.slice(0, 31 - suffix.length) + suffix;
  }
  taken.add(candidate.toLowerCase());
  return candidate;
}

export interface SurveyExportOptions {
  lang: SurveyExportLang;
  /** No name, phone, email or submission time; rows shuffled and labelled Response 1, 2, 3. */
  anonymous?: boolean;
  random?: () => number;
}

/** The workbook's content. Missing values stay blank, so nothing unanswered is counted. */
export function buildSurveyExport(levels: SurveyLevelInput[], respondents: SurveyRespondent[], options: SurveyExportOptions): SurveyExport {
  const { lang, anonymous = false } = options;
  const L = LABELS[lang];
  const taken = new Set<string>();
  const summaryName = uniqueSheetName(L.summary, taken);
  const allAnswersName = uniqueSheetName(L.allAnswers, taken);

  // Everyone who submitted a survey level, including those who left every question blank:
  // the samples list them with empty answers, which are not counted.
  const answering = respondents.filter(person =>
    levels.some(level => person.submissions[String(level.id)]));
  const firstSubmission = (person: SurveyRespondent) => Math.min(...levels
    .map(level => person.submissions[String(level.id)]?.submittedOn?.getTime() ?? Infinity));
  // Latest submission first, as in the sample; anyone without a date goes last.
  const latestFirst = (a: SurveyRespondent, b: SurveyRespondent) => {
    const [x, y] = [firstSubmission(a), firstSubmission(b)];
    return x === y ? 0 : x === Infinity ? 1 : y === Infinity ? -1 : y - x;
  };
  const ordered = anonymous
    ? shuffle(answering, options.random ?? Math.random)
    : [...answering].sort(latestFirst);
  const label = (person: SurveyRespondent, index: number) => anonymous
    ? L.response(index + 1)
    : String(person.name ?? '').trim() || (person.id != null && person.id !== '' ? L.participantId(String(person.id)) : L.response(index + 1));

  // The samples order the anonymous columns Response, Country, Gender, Age.
  const personHeaders = anonymous
    ? [L.responseHeader, L.country, L.gender, L.age]
    : [L.participant, L.phone, L.email, L.gender, L.age, L.country, L.submittedOn];
  const personCells = (person: SurveyRespondent, index: number, submittedOn: Date | null): SurveyCell[] => {
    const gender = blankIfMissing(localizeGender(person.gender, lang));
    const age = blankIfMissing(person.age);
    const rawCountry = blankIfMissing(person.country);
    const country = typeof rawCountry === 'string' && lang === 'ar' ? translateCountryName(rawCountry, true) : rawCountry;
    return anonymous
      ? [label(person, index), country, gender, age]
      : [label(person, index), blankIfMissing(person.phone), blankIfMissing(person.email), gender, age, country, submittedOn];
  };

  const allAnswers: SurveyExport['allAnswers'] = {
    name: allAnswersName,
    headers: [...personHeaders, L.surveyLevel, L.qNumber, L.question, L.questionType, L.answer],
    rows: [],
    levelColumn: personHeaders.length,
    qColumn: personHeaders.length + 1,
    answerColumn: personHeaders.length + 4,
  };
  const summaryRows: SurveySummaryRow[] = [];
  const levelSheets: SurveySheet[] = [];
  // All answers lists each participant's answers together, level by level, as in the sample.
  const answerRowsByPerson: SurveyCell[][][] = ordered.map(() => []);

  for (const level of levels) {
    // "Level 2", as in the samples: the same name as the level's sheet.
    const sheetName = uniqueSheetName(`${L.level} ${level.position}`, taken);
    const levelLabel = sheetName;
    const questions = (level.questions || []).map((question: any, index: number) => {
      const kind = questionKind(question);
      return {
        source: question,
        kind,
        q: `${L.qPrefix}${index + 1}`,
        text: String((lang === 'ar' ? question?.arabic_question_text || question?.question_text : question?.question_text || question?.arabic_question_text) ?? '').trim(),
        type: L[kind],
      };
    });
    const sheet: SurveySheet = {
      name: sheetName,
      headers: [...personHeaders, ...questions.map(question => `${question.q}: ${question.text}`)],
      rows: [],
    };
    // Per question: who answered, and how often each answer came up.
    const answered = questions.map(() => 0);
    const counts = questions.map(() => new Map<string, number>());

    ordered.forEach((person, index) => {
      const submission = person.submissions[String(level.id)];
      if (!submission) return;
      const matched = matchAnswers(questions.map(question => question.source), submission.answers);
      const answers = questions.map((question, qIndex) =>
        matched[qIndex] ? resolveAnswer(question.source, question.kind, matched[qIndex]!.value, lang) : []);
      const details = personCells(person, index, anonymous ? null : submission.submittedOn);
      sheet.rows.push([...details, ...answers.map(parts => parts.length ? parts.join(ANSWER_SEPARATOR) : null)]);
      questions.forEach((question, qIndex) => {
        const parts = answers[qIndex];
        if (parts.length) answered[qIndex]++;
        parts.forEach(part => counts[qIndex].set(part, (counts[qIndex].get(part) ?? 0) + 1));
        // One row per selected option, and a blank one for an unanswered question.
        for (const part of parts.length ? parts : [null]) {
          answerRowsByPerson[index].push([...details, levelLabel, question.q, question.text, question.type, part]);
        }
      });
    });

    questions.forEach((question, qIndex) => {
      const base = {
        level: levelLabel, q: question.q, question: question.text, type: question.type,
        answered: answered[qIndex], levelSheet: sheetName, levelColumn: personHeaders.length + qIndex,
      };
      if (question.kind === 'open') {
        summaryRows.push({ ...base, option: L.openOption, responses: answered[qIndex], percent: null, open: true });
        return;
      }
      const optionNames = [...new Set((question.source.options || []).map((option: any) => optionText(option, lang)).filter(Boolean))] as string[];
      // Answers that match no option still get a row, so the counts add up.
      const others = [...counts[qIndex].keys()].filter(name => !optionNames.includes(name));
      for (const option of [...optionNames, ...others]) {
        const responses = counts[qIndex].get(option) ?? 0;
        summaryRows.push({
          ...base, option, responses, open: false,
          percent: answered[qIndex] ? responses / answered[qIndex] : null,
        });
      }
    });
    levelSheets.push(sheet);
  }
  allAnswers.rows = answerRowsByPerson.flat();

  return {
    rtl: lang === 'ar',
    summary: {
      name: summaryName,
      title: L.title,
      note: anonymous ? L.anonymousNote : L.note(allAnswersName),
      headers: [L.surveyLevel, L.qNumber, L.question, L.questionType, L.answered, L.answerOption, L.responses, L.percent],
      rows: summaryRows,
    },
    allAnswers,
    levels: levelSheets,
  };
}

/** `Survey Responses - <campaign name> - <date>.xlsx`, keeping the name as written, Arabic included. */
export function surveyExportFileName(campaignName: string, date: Date = new Date()): string {
  const name = String(campaignName ?? '').replace(/[\\/:*?"<>|\u0000-\u001f]/g, ' ').replace(/\s+/g, ' ').trim();
  const day = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  return `Survey Responses - ${name ? `${name} - ` : ''}${day}.xlsx`;
}

function columnLetter(index: number): string {
  let letters = '';
  for (let n = index + 1; n > 0; n = Math.floor((n - 1) / 26)) {
    letters = String.fromCharCode(65 + ((n - 1) % 26)) + letters;
  }
  return letters;
}

const sheetRef = (name: string) => `'${name.replace(/'/g, "''")}'`;

// Excel stores a date without a zone, so the local wall-clock time is what goes in.
const asExcelDate = (date: Date) => new Date(date.getTime() - date.getTimezoneOffset() * 60000);

// Styling from the sample files.
const HEADER_FILL = 'FF4A22D4';
const HEADER_TEXT = 'FFFFFFFF';
const TITLE_TEXT = 'FF1B1534';
const NOTE_TEXT = 'FF5E5878';
const SUMMARY_WIDTHS = [13, 7, 46, 30, 11, 32, 12, 9];
const PERSON_WIDTHS = [22, 18, 22, 12, 10, 22, 18];
const ANONYMOUS_PERSON_WIDTHS = [16, 22, 12, 10];
const ANSWER_WIDTHS = [13, 7, 46, 30, 34];
const QUESTION_WIDTH = 40;

/** Styles the row's first `columns` cells, blank ones included, so the whole table reads alike. */
function styleRow(sheet: Worksheet, rowNumber: number, columns: number, header: boolean): void {
  const row = sheet.getRow(rowNumber);
  for (let column = 1; column <= columns; column++) {
    const cell = row.getCell(column);
    cell.font = header ? { bold: true, size: 11, color: { argb: HEADER_TEXT } } : { size: 11 };
    cell.alignment = { wrapText: true };
    if (header) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_FILL } };
  }
}

/** A header row and its data, filterable, with the header (and `xSplit` columns) frozen. */
function addTable(workbook: Workbook, table: SurveySheet, rtl: boolean, xSplit: number, widths: number[]): Worksheet {
  const sheet = workbook.addWorksheet(table.name, { views: [{ rightToLeft: rtl, state: 'frozen', xSplit, ySplit: 1 }] });
  sheet.addRow(table.headers);
  styleRow(sheet, 1, table.headers.length, true);
  table.rows.forEach(cells => {
    const row = sheet.addRow(cells.map(cell => cell instanceof Date ? asExcelDate(cell) : cell));
    cells.forEach((cell, index) => {
      if (cell instanceof Date) row.getCell(index + 1).numFmt = DATE_FORMAT;
    });
    styleRow(sheet, row.number, table.headers.length, false);
  });
  table.headers.forEach((_, index) => sheet.getColumn(index + 1).width = widths[index] ?? QUESTION_WIDTH);
  sheet.autoFilter = { from: { row: 1, column: 1 }, to: { row: table.rows.length + 1, column: table.headers.length } };
  return sheet;
}

/**
 * The exceljs Workbook class. exceljs is a CommonJS package, so in the production build the
 * dynamic import hands its exports back under `default` and a destructured `Workbook` is
 * undefined ("Workbook is not a constructor"); the dev server and tests expose it directly.
 */
export async function loadExcelWorkbook(): Promise<typeof Workbook> {
  const exceljs: any = await import('exceljs');
  const Workbook = exceljs.Workbook ?? exceljs.default?.Workbook;
  if (typeof Workbook !== 'function') throw new TypeError('exceljs did not load: Workbook is missing');
  return Workbook;
}

/** COUNTIFS reads a leading <, > or =and the wildcards * ? ~ in its criteria, so such option text needs an exact comparison. */
const countifsSafe = (text: string) => !/^[<>=]|[*?~]/.test(text);

/**
 * Writes the workbook. The Summary counts are formulas over the All answers and
 * level sheets (with the figures cached), so they follow any edit to the answers.
 */
export async function writeSurveyWorkbook(data: SurveyExport): Promise<ArrayBuffer> {
  const Workbook = await loadExcelWorkbook();
  const workbook = new Workbook();
  workbook.calcProperties.fullCalcOnLoad = true;
  const { summary, allAnswers, rtl } = data;
  const personColumns = allAnswers.levelColumn;

  const summarySheet = workbook.addWorksheet(summary.name, { views: [{ rightToLeft: rtl, state: 'frozen', ySplit: 4 }] });
  summarySheet.addRow([summary.title]).getCell(1).font = { bold: true, size: 14, color: { argb: TITLE_TEXT } };
  summarySheet.addRow([summary.note]).getCell(1).font = { size: 10, color: { argb: NOTE_TEXT } };
  summarySheet.mergeCells('A2:H2');
  summarySheet.addRow([]);
  summarySheet.addRow(summary.headers);
  styleRow(summarySheet, 4, summary.headers.length, true);

  const answersEnd = allAnswers.rows.length + 1;
  const answersRange = (column: number) => `${sheetRef(allAnswers.name)}!$${columnLetter(column)}$2:$${columnLetter(column)}$${answersEnd}`;
  const levelRows = new Map(data.levels.map(level => [level.name, level.rows.length + 1]));
  // Row of each question's first option; the question's later rows point back to its Answered cell.
  let questionRow = 0;
  let questionKey = '';

  summary.rows.forEach(row => {
    const r = summarySheet.rowCount + 1;
    const key = `${row.levelSheet}\u0000${row.q}`;
    const firstOfQuestion = key !== questionKey;
    if (firstOfQuestion) {
      questionKey = key;
      questionRow = r;
    }
    const levelEnd = levelRows.get(row.levelSheet) ?? 1;
    const levelColumn = columnLetter(row.levelColumn);
    const answered = !firstOfQuestion
      ? { formula: `E${questionRow}`, result: row.answered }
      : levelEnd > 1
        ? { formula: `COUNTIF(${sheetRef(row.levelSheet)}!$${levelColumn}$2:$${levelColumn}$${levelEnd},"?*")`, result: row.answered }
        : row.answered;
    let responses: any = row.responses;
    if (row.open) {
      responses = { formula: `E${r}`, result: row.responses };
    } else if (answersEnd > 1) {
      responses = {
        formula: countifsSafe(row.option)
          ? `COUNTIFS(${answersRange(allAnswers.levelColumn)},A${r},${answersRange(allAnswers.qColumn)},B${r},${answersRange(allAnswers.answerColumn)},F${r})`
          : `SUMPRODUCT((${answersRange(allAnswers.levelColumn)}=A${r})*(${answersRange(allAnswers.qColumn)}=B${r})*(${answersRange(allAnswers.answerColumn)}=F${r}))`,
        result: row.responses,
      };
    }
    const percent = row.open ? null : { formula: `IFERROR(G${r}/E${r},0)`, result: row.percent ?? 0 };
    summarySheet.addRow([row.level, row.q, row.question, row.type, answered, row.option, responses, percent]);
    styleRow(summarySheet, r, summary.headers.length, false);
    if (!row.open) summarySheet.getCell(`H${r}`).numFmt = '0%';
  });
  SUMMARY_WIDTHS.forEach((width, index) => summarySheet.getColumn(index + 1).width = width);
  // exceljs leaves out a column whose width is its default of 9 unless the column has a
  // style, which would leave % at Excel's narrower default.
  summarySheet.getColumn(8).numFmt = '0%';
  summarySheet.autoFilter = { from: { row: 4, column: 1 }, to: { row: summarySheet.rowCount, column: summary.headers.length } };

  const personWidths = personColumns === ANONYMOUS_PERSON_WIDTHS.length ? ANONYMOUS_PERSON_WIDTHS : PERSON_WIDTHS;
  addTable(workbook, allAnswers, rtl, 1, [...personWidths, ...ANSWER_WIDTHS]);
  data.levels.forEach(level => addTable(workbook, level, rtl, personColumns, personWidths));

  return workbook.xlsx.writeBuffer() as Promise<ArrayBuffer>;
}
